import { Award, Users, HeartHandshake, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FOUNDER_IMAGE } from "@/components/landing/content";

const VALUES = [
  {
    Icon: Award,
    title: "Results first",
    desc: "Every strategy is measured by real customer growth — not vanity metrics.",
  },
  {
    Icon: Users,
    title: "Local insight",
    desc: "We live and work in Kentucky and know how to reach Kentucky customers.",
  },
  {
    Icon: HeartHandshake,
    title: "Partnership",
    desc: "You’re not just a client. We treat your business like our own.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-slate-50 border-y border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 flex justify-center lg:justify-start">
          <div className="relative w-full max-w-[360px]">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-orange-200 via-white to-blue-200 -z-10" />
            <div className="rounded-[2rem] overflow-hidden border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)] aspect-[4/5]">
              <img
                src={FOUNDER_IMAGE}
                alt="Julian Tovar, founder of JT Brand Scales"
                loading="lazy"
                className="w-full h-full object-cover object-center"
                data-testid="about-founder-image"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 rounded-2xl bg-blue-700 text-white px-5 py-4 shadow-lg">
              <div className="font-display font-extrabold text-lg leading-none">
                Julian Tovar
              </div>
              <div className="text-xs text-blue-100 mt-1 tracking-wide">
                Founder · JT Brand Scales
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <span className="inline-block text-xs uppercase tracking-[0.22em] font-semibold text-orange-600">
            About
          </span>
          <h2
            data-testid="about-heading"
            className="font-display mt-3 text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]"
          >
            Built in Kentucky.{" "}
            <span className="brand-text-gradient">Built for growth.</span>
          </h2>
          <div className="mt-6 space-y-5 text-slate-700 font-body text-lg leading-relaxed">
            <p>
              Hi, I’m <strong className="text-slate-900">Julian Tovar</strong>,
              the founder of JT Brand Scales. I started this studio with one
              goal: help local businesses stand out and reach more customers
              through smart marketing strategies.
            </p>
            <p>
              A lot of great businesses struggle — not because they lack
              quality, but because they aren’t getting enough visibility online.
              That’s where we come in. We build stronger brands, sharper
              messaging and marketing systems that turn attention into real
              customers.
            </p>
            <p className="text-slate-900 font-semibold">
              At JT Brand Scales, you’re not just another client — you’re a
              partner, and your success matters.
            </p>
          </div>

          <ul className="mt-10 grid sm:grid-cols-3 gap-4">
            {VALUES.map(({ Icon, title, desc }) => (
              <li
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
                  <Icon size={18} />
                </span>
                <h3 className="mt-3 font-display font-extrabold text-slate-900">
                  {title}
                </h3>
                <p className="mt-1 text-sm text-slate-600">{desc}</p>
              </li>
            ))}
          </ul>

          <Button
            asChild
            data-testid="about-cta"
            className="mt-10 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 h-12 btn-shine"
          >
            <a href="#contact">
              Let’s grow together <ArrowRight size={18} className="ml-1" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
